/**
 * useEntityData — 实体数据列表 + 元信息 Hook
 *
 * 从 GET /api/v1/entities/:slug      获取 EntityMeta
 * 从 GET /api/v1/entities/:slug (paginated) 获取行数据
 * 结合 useViewTemplate 生成 DSL 列定义
 *
 * 适用于所有 TMS/WMS/FMS 的列表页面
 */

import { useEffect, useState, useCallback } from 'react';
import { api, type PaginatedData } from '@/lib/api';
import { useViewTemplate, type ViewTemplateDSL, type ViewType } from '@/lib/use-view-template';
import type { EntityMeta } from '@/types/entity';

// ─── Types ─────────────────────────────────────────

interface UseEntityDataOptions {
  slug: string;
  /** Override which DSL template slug to resolve columns from (defaults to slug) */
  templateSlug?: string;
  viewType?: ViewType;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  filters?: Record<string, string>;
  search?: string;
}

interface UseEntityDataResult {
  meta: EntityMeta | null;
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  hasMore: boolean;
  template: ViewTemplateDSL | null;
  loading: boolean;
  metaLoading: boolean;
  error: string | null;
  refresh: () => void;
  setPage: (p: number) => void;
}

// ─── Hook ──────────────────────────────────────────

export function useEntityData(opts: UseEntityDataOptions): UseEntityDataResult {
  const [meta, setMeta] = useState<EntityMeta | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(opts.page ?? 1);
  const [hasMore, setHasMore] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get view template DSL (depends on meta) — templateSlug overrides slug for column lookup
  const { template } = useViewTemplate(opts.templateSlug ?? opts.slug, opts.viewType ?? 'list', meta);

  // Fetch entity meta
  useEffect(() => {
    let cancelled = false;
    setMetaLoading(true);

    api.get<EntityMeta>(`/entities/${opts.slug}`)
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load entity meta');
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });

    return () => { cancelled = true; };
  }, [opts.slug]);

  // Fetch entity rows
  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(opts.limit ?? 20),
        includeTotal: 'false',
      });
      if (opts.sort) params.set('sort', opts.sort);
      if (opts.order) params.set('order', opts.order);
      if (opts.search) params.set('search', opts.search);
      if (opts.filters) {
        for (const [k, v] of Object.entries(opts.filters)) {
          params.set(`filters[${k}]`, v);
        }
      }

      const result = await api.get<PaginatedData<Record<string, unknown>>>(
        `/entities/${opts.slug}?${params.toString()}`
      );
      setRows(result.items ?? []);
      setTotal(result.total ?? 0);
      setHasMore(result.hasMore ?? false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [opts.slug, currentPage, opts.limit, opts.sort, opts.order, opts.search, JSON.stringify(opts.filters)]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  return {
    meta,
    rows,
    total,
    page: currentPage,
    hasMore,
    template,
    loading,
    metaLoading,
    error,
    refresh: fetchRows,
    setPage: setCurrentPage,
  };
}
